/* Keep PDP size choices in sync with the native theme variant renderer. */
class MainroseProductPage extends HTMLElement {
  connectedCallback() {
    this.chosen = new Set();
    this.resolving = false;
    this.abort = new AbortController();
    this.root = this.closest('.shopify-section--main-product');
    this.addEventListener('change', async event => {
      const select = event.target.closest('[data-mr-size-select]');
      if (!select || this.resolving) return;
      const picker = select.closest('variant-picker');
      const radio = [...select.parentElement.querySelectorAll('input[type="radio"]')].find(input => input.value === select.value);
      if (!radio || !picker) return;
      const form = document.forms.namedItem(picker.getAttribute('form-id'));
      radio.checked = true;
      this.chosen.add(select.dataset.optionPosition);
      this.resolving = true;
      this.update();
      const options = [...form.elements].filter(input => input.matches('input[data-option-position]:checked')).sort((a,b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition)).map(input => input.value);
      try {
        await picker.selectCombination({optionValues: options, productChange: radio.hasAttribute('data-product-url')});
        this.querySelector('[data-mr-size-error]')?.remove();
      } catch (error) {
        this.chosen.delete(select.dataset.optionPosition);
        if (!this.querySelector('[data-mr-size-error]')) {
          const message = document.createElement('p');
          message.dataset.mrSizeError = '';
          message.setAttribute('role', 'alert');
          message.className = 'text-sm';
          message.textContent = 'Unable to update this size. Please try again.';
          this.querySelector('.product-info__purchase')?.prepend(message);
        }
        console.error('Main Rose size selection failed', error);
      } finally {
        this.resolving = false;
        if (this.isConnected) this.update();
      }
    }, {signal: this.abort.signal});
    this.observer = new MutationObserver(() => this.update());
    this.observer.observe(this, {childList: true, subtree: true});
    this.update();
  }
  update() {
    const title = this.querySelector('.product-info__block-item[data-block-type="title"]');
    const wishlist = this.querySelector('.product-info__block-item[data-block-type="@app"]:has(wishlist-button-block)');
    if (title && wishlist && wishlist.parentElement !== title) title.append(wishlist);
    const picker = this.querySelector('variant-picker[context="main_product"]');
    const fields = [...(picker?.querySelectorAll('[data-option-role="size"]') || [])];
    const pending = fields.some(field => !this.chosen.has(field.dataset.optionPosition));
    const variant = JSON.parse(picker?.querySelector('[data-variant]')?.textContent || 'null');
    this.root.classList.toggle('sizes-pending-selection', pending || this.resolving);
    const formId = picker?.getAttribute('form-id') || this.querySelector('.product-info__purchase form')?.getAttribute('id');
    const form = document.forms.namedItem(formId);
    this.root.querySelectorAll('button[type="submit"]').forEach(button => {
      if (button.form?.getAttribute('id') === formId) button.disabled = pending || this.resolving || (variant ? !variant.available : button.disabled);
    });
    this.querySelectorAll('.product-info__purchase .shopify-payment-button').forEach(button => { button.inert = pending || this.resolving; });
    fields.forEach(field => {
      const position = field.dataset.optionPosition;
      const select = field.querySelector('[data-mr-size-select]');
      if (!select) return;
      const selected = [...(form?.elements || [])].find(input => input.matches('input[data-option-position]:checked') && input.dataset.optionPosition === position);
      select.value = this.chosen.has(position) ? selected?.value || '' : '';
      select.disabled = this.resolving;
      select.classList.toggle('mr-size-pending', !this.chosen.has(position));
      select.setAttribute('aria-busy', String(this.resolving));
    });
  }
  disconnectedCallback() {
    this.abort.abort();
    this.observer.disconnect();
    this.root.classList.remove('sizes-pending-selection');
  }
}
if (!customElements.get('mr-product-page')) customElements.define('mr-product-page', MainroseProductPage);

/* Photo autoplay uses the native carousel; touch/keyboard interaction pauses it. */
class MainroseGalleryControls extends HTMLElement {
  async connectedCallback() {
    this.abort = new AbortController();
    const signal = this.abort.signal;
    await customElements.whenDefined('scroll-carousel');
    if (!this.isConnected) return;
    this.carousel = document.getElementById(this.getAttribute('aria-controls'));
    if (!this.carousel) return;
    this.mobile = matchMedia('(max-width: 999px)');
    this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
    this.paused = this.reduced.matches;
    this.visible = false;
    this.pills = [...this.querySelectorAll('[data-media-id]')];
    this.toggle = this.querySelector('.mr-gallery-controls__toggle');
    this.pills.forEach(button => button.addEventListener('click', () => {
      this.paused = true;
      const index = this.carousel.cells.findIndex(cell => cell.dataset.mediaId === button.dataset.mediaId);
      this.carousel.select(index, {instant: this.reduced.matches});
      this.sync();
    }, {signal}));
    this.toggle.addEventListener('click', () => { this.paused = !this.paused; this.sync(); }, {signal});
    this.carousel.addEventListener('pointerdown', () => { this.paused = true; this.sync(); }, {signal, passive: true});
    this.carousel.addEventListener('keydown', () => { this.paused = true; this.sync(); }, {signal});
    this.carousel.addEventListener('carousel:change', () => this.sync(), {signal});
    this.carousel.addEventListener('carousel:filter', () => this.sync(), {signal});
    this.carousel.closest('product-gallery').addEventListener('lightbox:open', () => { this.paused = true; this.sync(); }, {signal});
    document.addEventListener('visibilitychange', () => this.sync(), {signal});
    this.mobile.addEventListener('change', () => this.sync(), {signal});
    this.reduced.addEventListener('change', () => { this.paused = this.reduced.matches; this.sync(); }, {signal});
    this.observer = new IntersectionObserver(entries => {
      this.visible = entries[0].intersectionRatio >= .55;
      this.sync();
    }, {threshold: [0, .55]});
    this.observer.observe(this.carousel);
    this.sync();
  }
  sync() {
    clearTimeout(this.timer);
    this.progress?.cancel();
    const cells = this.carousel.cells;
    const selected = this.carousel.selectedCell;
    this.pills.forEach(pill => {
      pill.hidden = !cells.some(cell => cell.dataset.mediaId === pill.dataset.mediaId);
      pill.setAttribute('aria-current', String(pill.dataset.mediaId === selected?.dataset.mediaId));
    });
    const playing = !this.paused && !this.reduced.matches && this.mobile.matches && this.visible && !document.hidden && selected?.dataset.mediaType === 'image' && cells.length > 1;
    this.dataset.playing = String(playing);
    this.toggle.setAttribute('aria-label', this.paused ? 'Play image slideshow' : 'Pause image slideshow');
    this.toggle.setAttribute('aria-pressed', String(this.paused));
    this.toggle.hidden = this.reduced.matches;
    if (!playing) return;
    const progress = this.querySelector('[aria-current="true"] span');
    this.progress = progress?.animate([{transform: 'scaleX(0)'}, {transform: 'scaleX(1)'}], {duration: 5000, easing: 'linear', fill: 'forwards'});
    this.timer = setTimeout(() => {
      const next = (this.carousel.selectedIndex + 1) % cells.length;
      this.carousel.select(next, {instant: next === 0});
      this.sync();
    }, 5000);
  }
  disconnectedCallback() {
    this.abort.abort();
    this.observer?.disconnect();
    this.progress?.cancel();
    clearTimeout(this.timer);
  }
}
if (!customElements.get('mr-pdp-gallery-controls')) customElements.define('mr-pdp-gallery-controls', MainroseGalleryControls);
